"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t } from "@/lib/i18n/interpolate";
import {
  type ElderV,
  fromRunes,
  RUNE_ALPHABETS,
  type RuneAlphabet,
  type RuneOptions,
  runeRows,
  toRunes,
  type WordSeparator,
  type YoungerE,
  type YoungerO,
} from "@/lib/tool-kit/transliteration-more/runes";

/** English strings. A translated content file overrides these through `ui`. */
export const RUNES_TRANSLATOR_STRINGS = {
  direction: "Direction",
  toRunes: "Latin → Runes",
  toLatin: "Runes → Latin",
  notice:
    "This is transliteration, not translation: it spells your letters with runes. It does not turn modern English into Old Norse or Old English.",
  inputLabel: "Text to write in runes",
  inputHint:
    "Latin letters. Capitals and accents are folded; þ, ð, ŋ, æ and ï are understood. Numbers and punctuation pass through.",
  runesInputLabel: "Runes",
  runesInputHint: "Paste runes from the Runic block of Unicode.",
  alphabet: "Alphabet to read",
  samples: {
    latin: "Wulfstan wrote these runes",
    elder: "ᚠᚢᚦᚨᚱᚲ ᚷᚹ ᚺᚾᛁᛃᛇᛈᛉᛊ",
    younger: "ᚦᚢᚱ ᚢᛁᚴᛁ ᚴᛅᚱᚦᛁ",
    futhorc: "ᚹᚢᛚᚠᛋᛏᚪᚾ ᚹᚱᚪᛏ",
  },
  sample: "Insert a sample",
  optionsLegend: "Options",
  separatorLabel: "Between words",
  separatorOptions: { space: "A space", dot: "The runic word dot ᛫" },
  elderVLabel: "Elder Futhark: v is written",
  elderVOptions: {
    wunjo: "ᚹ wunjo (as w)",
    uruz: "ᚢ uruz (as u)",
    fehu: "ᚠ fehu (as f)",
  },
  youngerOLabel: "Younger Futhark: o is written",
  youngerOOptions: { uruz: "ᚢ úr (the usual rune)", oss: "ᚬ óss" },
  youngerELabel: "Younger Futhark: e is written",
  youngerEOptions: { isaz: "ᛁ íss (as i)", ar: "ᛅ ár (as a)" },
  futhorcDiphthongs:
    "Futhorc: write the letter pairs ea and io with the runes ᛠ and ᛡ",
  resultsHeading: "In runes",
  latinHeading: "In Latin letters",
  alphabets: {
    elder: "Elder Futhark",
    younger: "Younger Futhark (long-branch)",
    futhorc: "Anglo-Saxon Futhorc",
  },
  uses: {
    elder:
      "24 runes, 2nd to 8th century. Early Germanic and Proto-Norse inscriptions.",
    younger:
      "16 runes, 8th to 12th century, the Viking Age. One rune covers several sounds.",
    futhorc:
      "28 to 33 runes, 5th to 11th century. Old English and Frisian inscriptions.",
  },
  enterText: "Type or paste some text to convert.",
  unknownLetters:
    "Left unchanged because the alphabet has no value for them: {chars}.",
  unknownRunes:
    "Left unchanged because this alphabet does not use them: {chars}.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "The runes",
  tableAlphabet: "Alphabet for the table",
  runeColumn: "Rune",
  letterColumn: "Letter",
  nameColumn: "Name",
  tableNote:
    "Letters the alphabet lacks are spelled with the nearest rune: c and k with ᚲ in the Elder Futhark, q with ᚲᚹ, x with ᚲᛊ; d and t share ᛏ in the Younger Futhark, as do b and p (ᛒ) and k and g (ᚴ).",
} as const;

type Strings = typeof RUNES_TRANSLATOR_STRINGS;
type Direction = "to-runes" | "to-latin";

const DEFAULT_OPTIONS: RuneOptions = {
  elderV: "wunjo",
  youngerO: "uruz",
  youngerE: "isaz",
  futhorcDiphthongs: false,
  separator: "space",
};

function RuneTable({ strings }: { strings: Strings }) {
  const [alphabet, setAlphabet] = useState<RuneAlphabet>("elder");
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <div className="max-w-sm">
        <SelectField<RuneAlphabet>
          id="rn-table-alphabet"
          label={strings.tableAlphabet}
          onChange={setAlphabet}
          options={RUNE_ALPHABETS.map((each) => ({
            value: each,
            label: strings.alphabets[each],
          }))}
          value={alphabet}
        />
      </div>
      <DataTable
        head={[strings.runeColumn, strings.letterColumn, strings.nameColumn]}
        rows={runeRows(alphabet).map((row) => [row.rune, row.letter, row.name])}
      />
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

function RuneOptionsFields({
  options,
  setOptions,
  strings,
}: {
  options: RuneOptions;
  setOptions: (update: (o: RuneOptions) => RuneOptions) => void;
  strings: Strings;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className={LABEL}>{strings.optionsLegend}</legend>
      <div className="grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField<WordSeparator>
          id="rn-separator"
          label={strings.separatorLabel}
          onChange={(separator) => setOptions((o) => ({ ...o, separator }))}
          options={[
            { value: "space", label: strings.separatorOptions.space },
            { value: "dot", label: strings.separatorOptions.dot },
          ]}
          value={options.separator}
        />
        <SelectField<ElderV>
          id="rn-elder-v"
          label={strings.elderVLabel}
          onChange={(elderV) => setOptions((o) => ({ ...o, elderV }))}
          options={[
            { value: "wunjo", label: strings.elderVOptions.wunjo },
            { value: "uruz", label: strings.elderVOptions.uruz },
            { value: "fehu", label: strings.elderVOptions.fehu },
          ]}
          value={options.elderV}
        />
        <SelectField<YoungerO>
          id="rn-younger-o"
          label={strings.youngerOLabel}
          onChange={(youngerO) => setOptions((o) => ({ ...o, youngerO }))}
          options={[
            { value: "uruz", label: strings.youngerOOptions.uruz },
            { value: "oss", label: strings.youngerOOptions.oss },
          ]}
          value={options.youngerO}
        />
        <SelectField<YoungerE>
          id="rn-younger-e"
          label={strings.youngerELabel}
          onChange={(youngerE) => setOptions((o) => ({ ...o, youngerE }))}
          options={[
            { value: "isaz", label: strings.youngerEOptions.isaz },
            { value: "ar", label: strings.youngerEOptions.ar },
          ]}
          value={options.youngerE}
        />
      </div>
      <Checkbox
        checked={options.futhorcDiphthongs}
        label={strings.futhorcDiphthongs}
        onChange={(futhorcDiphthongs) =>
          setOptions((o) => ({ ...o, futhorcDiphthongs }))
        }
      />
    </fieldset>
  );
}

function ToRunesResults({
  text,
  options,
  strings,
}: {
  text: string;
  options: RuneOptions;
  strings: Strings;
}) {
  const results = RUNE_ALPHABETS.map((alphabet) => ({
    alphabet,
    result: toRunes(text, alphabet, options),
  }));
  const unknown = [...new Set(results.flatMap((each) => each.result.unknown))];
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownLetters, { chars: unknown.join(" ") })}
        </Notice>
      ) : null}
      <div className="grid gap-4 md:grid-cols-3">
        {results.map(({ alphabet, result }) => (
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            key={alphabet}
            lang="und-Runr"
            text={result.text}
            title={strings.alphabets[alphabet]}
            use={strings.uses[alphabet]}
          />
        ))}
      </div>
    </section>
  );
}

function ToLatinResults({
  text,
  alphabet,
  strings,
}: {
  text: string;
  alphabet: RuneAlphabet;
  strings: Strings;
}) {
  const result = fromRunes(text, alphabet);
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.latinHeading}</h2>
      {result.unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownRunes, { chars: result.unknown.join(" ") })}
        </Notice>
      ) : null}
      <ResultCard
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        lang="en"
        text={result.text}
        title={strings.alphabets[alphabet]}
        use={strings.uses[alphabet]}
      />
    </section>
  );
}

export default function RunesTranslatorWidget() {
  const strings = useToolUi(RUNES_TRANSLATOR_STRINGS);
  const [direction, setDirection] = useState<Direction>("to-runes");
  const [alphabet, setAlphabet] = useState<RuneAlphabet>("elder");
  const [latin, setLatin] = useState<string>(strings.samples.latin);
  const [runes, setRunes] = useState<string>(strings.samples.elder);
  const [options, setOptions] = useState<RuneOptions>(DEFAULT_OPTIONS);
  const forward = direction === "to-runes";
  const current = forward ? latin : runes;

  return (
    <div className="space-y-6">
      <Notice>{strings.notice}</Notice>
      <PillGroup
        label={strings.direction}
        name="rn-direction"
        onChange={setDirection}
        options={[
          { value: "to-runes", label: strings.toRunes },
          { value: "to-latin", label: strings.toLatin },
        ]}
        value={direction}
      />
      {forward ? (
        <ScriptTextArea
          dir="ltr"
          hint={strings.inputHint}
          id="rn-input"
          label={strings.inputLabel}
          lang="en"
          onChange={setLatin}
          value={latin}
        />
      ) : (
        <>
          <PillGroup
            label={strings.alphabet}
            name="rn-alphabet"
            onChange={setAlphabet}
            options={RUNE_ALPHABETS.map((each) => ({
              value: each,
              label: strings.alphabets[each],
            }))}
            value={alphabet}
          />
          <ScriptTextArea
            dir="ltr"
            hint={strings.runesInputHint}
            id="rn-runes-input"
            label={strings.runesInputLabel}
            lang="und-Runr"
            onChange={setRunes}
            value={runes}
          />
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            forward
              ? setLatin(strings.samples.latin)
              : setRunes(strings.samples[alphabet])
          }
          type="button"
        >
          {strings.sample}
        </button>
      </div>
      {forward ? (
        <RuneOptionsFields
          options={options}
          setOptions={setOptions}
          strings={strings}
        />
      ) : null}
      {current.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
      {current.trim() !== "" && forward ? (
        <ToRunesResults options={options} strings={strings} text={latin} />
      ) : null}
      {current.trim() !== "" && !forward ? (
        <ToLatinResults alphabet={alphabet} strings={strings} text={runes} />
      ) : null}
      <RuneTable strings={strings} />
    </div>
  );
}
